import { useCallback } from 'react';
import { useBaseConfig } from './contexts';
import { featureFlagsDefinition } from './featureFlagsDefinition';
import { useCookie } from './hooks/useCookie';
import { devError } from './utils';
// define feature flags in featureFlagsDefinition.ts!
export const featureFlags = featureFlagsDefinition;
// get info about a feature flag
// the main, exported function is only used as a wrapper to turn the generic feature flag key
// into a specific single feature flag key
function getFlagInfo_internal(key) {
    const flag = featureFlagsDefinition[key];
    if (!flag) {
        devError(`Feature flag ${key} not found`);
        return null;
    }
    return flag;
}
/**
 * Get info about a feature flag.
 *
 * @param key feature flag key
 * @returns FeatureFlag
 */
export function getFlagInfo(key) {
    return getFlagInfo_internal(key);
}
// TODO: wrap all of these with a generic function that makes featureFlagsDefinition generic
/**
 * Resolve a feature flag key to its value based on a flag bag (client cookies) and static config (env vars).
 *
 * @param flagBag values of flags
 * @param key key of a flag
 * @returns value of flag
 */
function resolveFlag(flagBag, key, config) {
    const flag = featureFlagsDefinition[key];
    if (!flag) {
        // the type system should guarantee that this never happens
        throw new Error(`Feature flag ${key} not found`);
    }
    for (const source of flag.sources) {
        switch (source) {
            case 'client': {
                if (config.debug) {
                    if (key in flagBag && flagBag[key] !== undefined) {
                        // we are getting the type from the definition, so we can be sure that the type is correct here
                        return flagBag[key];
                    }
                }
                break;
            }
            case 'envvar':
                if (key in config.featureFlagEnvVars &&
                    config.featureFlagEnvVars[key] !== undefined) {
                    // we are getting the type from the definition, so we can be sure that the type is correct here
                    return config.featureFlagEnvVars[key];
                }
                break;
            default:
                devError(`Unknown feature flag source: ${source}`);
        }
    }
    return flag.defaultValue;
}
/**
 * Resolves feature flag keys to its values based on a flag bag (client cookies) and static config (env vars).
 *
 * @param flagBag values of flags
 * @param keys keys of a flags
 * @returns values of flags
 */
function resolveFlags(flagBag, keys, config) {
    const flags = {};
    keys.forEach((key) => {
        flags[key] = resolveFlag(flagBag, key, config);
    });
    return flags;
}
// get the value of a feature flag
// the main, exported function is only used as a wrapper to turn the generic feature flag key
// into a specific single feature flag key
function useFlag_internal(key) {
    const [flagBag] = useCookie('featureFlags', {});
    const config = useBaseConfig();
    return resolveFlag(flagBag, key, config);
}
function useFlags_internal(keys) {
    const [flagBag] = useCookie('featureFlags', {});
    const config = useBaseConfig();
    return resolveFlags(flagBag, keys, config);
}
/**
 * Get the value of a feature flag.
 *
 * @param key feature flag key
 * @returns value
 */
export function useFlag(key) {
    return useFlag_internal(key);
}
/**
 * Get the values of feature flags.
 *
 * @param key feature flag keys
 * @returns a Record
 */
export function useFlags(keys) {
    return useFlags_internal(keys);
}
/**
 * Get all feature flags
 *
 * @returns a Record
 */
export function useGetAllFlags() {
    return useFlags_internal(Object.keys(featureFlagsDefinition));
}
/**
 * Get all feature flags set by the client
 *
 * @returns FeatureFlagBag of client-set feature flags
 */
export function useClientFlagBag() {
    const [flagBag] = useCookie('featureFlags', {});
    return flagBag;
}
/**
 * Returns a function to enable or disable a feature flag
 *
 * @param key feature flag key
 * @returns function to enable or disable a feature flag
 */
export function useSetFlag(key) {
    const [flagBag, setFlagBag] = useCookie('featureFlags', {});
    return useCallback((value) => {
        const flag = featureFlagsDefinition[key];
        if (!flag) {
            devError(`Feature flag ${key} not found`);
            return;
        }
        if (!flag.sources.includes('client')) {
            devError(`Feature flag ${key} does not support client`);
            return;
        }
        setFlagBag({ ...flagBag, [key]: value });
    }, [key, flagBag, setFlagBag]);
}
/**
 * Returns a function to reset all feature flags to their default values
 *
 * @returns function to reset all feature flags to their default values
 */
export function useResetFlags() {
    const [, setFlagBag] = useCookie('featureFlags', {});
    return useCallback(() => {
        setFlagBag({});
    }, [setFlagBag]);
}
/**
 * Creates a function to get the value of a feature flag by its key.
 *
 * @param flagBag values of flags
 * @param config config object
 * @returns a function to get the value of a feature flag by key
 */
export function createGetFlag(flagBag, config) {
    return function getFlag(key) {
        return resolveFlag(flagBag, key, config);
    };
}
